import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { ArrowLeft } from "lucide-react"
import { useMemo } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { publicGetEntry } from "@/http/changelog/public"
import { sanitizeHtml } from "@/lib/sanitize-html"

const publicEntryQueryOptions = (
  serviceSlug: string,
  entrySlug: string,
) =>
  queryOptions({
    queryKey: ["public-changelog-entry", serviceSlug, entrySlug],
    queryFn: () => publicGetEntry(serviceSlug, entrySlug),
  })

export const Route = createFileRoute(
  "/changelog/$serviceSlug/$entrySlug",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      publicEntryQueryOptions(
        params.serviceSlug,
        params.entrySlug,
      ),
    )
  },
  component: PublicEntryPage,
})

function PublicEntryPage() {
  const { serviceSlug, entrySlug } = Route.useParams()

  const { data: entry } = useSuspenseQuery(
    publicEntryQueryOptions(serviceSlug, entrySlug),
  )

  const sanitizedContent = useMemo(
    () =>
      entry?.content ? sanitizeHtml(entry.content) : null,
    [entry?.content],
  )

  const formattedDate = entry?.publishedAt
    ? format(
        new Date(entry.publishedAt),
        "dd 'de' MMMM, yyyy",
        { locale: ptBR },
      )
    : null

  return (
    <div className="min-h-svh">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-12 lg:py-16">
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="mb-8"
        >
          <Link
            to="/changelog/$serviceSlug"
            params={{ serviceSlug }}
          >
            <ArrowLeft className="mr-1.5 size-4" />
            Voltar ao changelog
          </Link>
        </Button>

        <article className="space-y-4">
          {formattedDate && (
            <time className="text-muted-foreground block text-sm font-medium">
              {formattedDate}
            </time>
          )}

          {entry && entry.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {entry.tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
                  style={{
                    borderColor: tag.color,
                    color: tag.color,
                  }}
                >
                  <span
                    className="inline-block size-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </Badge>
              ))}
            </div>
          )}

          <h1 className="text-3xl font-bold tracking-tight">
            {entry?.title}
          </h1>

          {sanitizedContent && (
            <div
              className="prose prose-neutral dark:prose-invert prose-headings:tracking-tight prose-img:rounded-lg prose-img:shadow-md mt-6 max-w-none leading-relaxed"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: content is sanitized with DOMPurify via sanitizeHtml
              dangerouslySetInnerHTML={{
                // biome-ignore lint/style/useNamingConvention: React API
                __html: sanitizedContent,
              }}
            />
          )}
        </article>
      </div>

      <footer className="border-t py-6 text-center">
        <p className="text-muted-foreground/60 text-xs">
          Powered by HEROw
        </p>
      </footer>
    </div>
  )
}
